import {
	type PaginationState,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import type {
	ColumnDef,
	ExpandedState,
	FilterFn,
	OnChangeFn,
	Row,
	SortingState,
	VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
	type DependencyList,
	type FC,
	Fragment,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import styled, { css } from 'styled-components'
import { genericMemo } from 'types/helpers'

import SortDownIcon from 'assets/svg/app/caret-down.svg'
import SortUpIcon from 'assets/svg/app/caret-up.svg'
import Loader from 'components/Loader'
import { Body } from 'components/Text'
import { FlexDiv } from 'components/layout/flex'
import { selectLeaderboardPageSize } from 'state/futures/selectors'
import { useAppSelector } from 'state/hooks'
import media from 'styles/media'

import { DRAG_HANDLE_CLASS_NAME } from 'components/GridLayout'
import Pagination, { type PaginationProps } from './Pagination'
import TableBodyRow, { TableCell } from './TableBodyRow'

const HEADER_HEIGHT = '30px'
const MAX_PAGE_ROWS = 100
const MAX_TOTAL_ROWS = 9999
const SHORT_PAGE_SIZE = 5

function calculatePageSize(
	showPagination: boolean,
	showShortList: boolean | undefined,
	pageSize: number | undefined
): number {
	if (showPagination) {
		return pageSize ? pageSize : MAX_PAGE_ROWS
	}
	return showShortList ? pageSize ?? SHORT_PAGE_SIZE : MAX_TOTAL_ROWS
}

export type GridLayoutStyles = {
	row?: string
	cell?: string
}

type TableProps<T> = {
	data: T[]
	columns: ColumnDef<T, any>[]
	onTableRowClick?: (row: Row<T>) => void
	onPaginationChange?: (pagination: PaginationState) => void
	className?: string
	isLoading?: boolean
	noResultsMessage?: ReactNode
	showPagination?: boolean
	pageSize?: number
	dynamicPageSize?: boolean
	hideHeaders?: boolean
	highlightRowsOnHover?: boolean
	sortBy?: SortingState
	showShortList?: boolean
	lastRef?: any
	paginationSize?: 'xs' | 'sm' | 'md'
	rounded?: boolean
	noBottom?: boolean
	noRowBorder?: boolean
	columnVisibility?: VisibilityState
	columnsDeps?: DependencyList
	autoResetPageIndex?: boolean
	paginationExtra?: ReactNode
	CustomPagination?: FC<PaginationProps>
	gridLayout?: GridLayoutStyles
	globalFilter?: string
	globalFilterFn?: FilterFn<T>
	SubRowComponent?: FC<{ row: Row<T> }>
	useVirtual?: boolean
	fetchNextPage?: () => void
	containerHeight?: number
	rowHeight?: number
}

const Table = <T,>({
	columns = [],
	data = [],
	noResultsMessage = null,
	onTableRowClick = undefined,
	onPaginationChange = undefined,
	isLoading = false,
	className,
	showPagination = false,
	pageSize = undefined,
	dynamicPageSize = false,
	hideHeaders,
	highlightRowsOnHover,
	showShortList,
	sortBy = [],
	paginationSize = 'md',
	rounded = true,
	noBottom = false,
	columnVisibility,
	autoResetPageIndex = true,
	columnsDeps = [],
	paginationExtra,
	CustomPagination,
	noRowBorder,
	gridLayout = {},
	globalFilter,
	globalFilterFn,
	SubRowComponent,
	useVirtual = false,
	fetchNextPage,
	containerHeight,
	rowHeight = Number.parseInt(HEADER_HEIGHT),
}: TableProps<T>) => {
	const [sorting, setSorting] = useState<SortingState>(sortBy)
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: calculatePageSize(showPagination, showShortList, pageSize),
	})
	const [expanded, setExpanded] = useState<ExpandedState>({})

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const memoizedColumns = useMemo(() => columns, columnsDeps)

	useEffect(() => {
		onPaginationChange?.(pagination)
	}, [onPaginationChange, pagination])

	const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
		setSorting(updater)
		if (table.getRowModel().rows.length) {
			rowVirtualizer?.scrollToIndex(0)
		}
	}

	const table = useReactTable<T>({
		columns: memoizedColumns,
		data,
		enableHiding: true,
		autoResetPageIndex,
		state: { sorting, columnVisibility, pagination, globalFilter, expanded },
		onExpandedChange: setExpanded,
		onPaginationChange: setPagination,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getRowCanExpand: () => true,
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: globalFilterFn,
	})

	table.setOptions((prev) => ({
		...prev,
		onSortingChange: handleSortingChange,
	}))

	const { rows } = table.getRowModel()
	const headerGroups = table.getHeaderGroups()

	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		estimateSize: () => rowHeight,
		getScrollElement: () => tableContainerRef.current,
		measureElement:
			typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
				? (element) => element?.getBoundingClientRect().height
				: undefined,
		overscan: 2,
	})

	const leaderboardPageSize = useAppSelector(selectLeaderboardPageSize)

	useEffect(() => {
		if (dynamicPageSize) {
			setPagination((prev) => ({
				...prev,
				pageSize: leaderboardPageSize,
			}))
		}
	}, [dynamicPageSize, leaderboardPageSize])

	const shouldShowPagination = useMemo(
		() => showPagination || (!showShortList && data.length > table.getState().pagination.pageSize),
		[data.length, showPagination, showShortList, table]
	)

	const handleRowClick = useCallback(
		(row: Row<T>) => () => {
			onTableRowClick?.(row)
		},
		[onTableRowClick]
	)

	const tableContainerRef = useRef<HTMLDivElement>(null)

	const fetchMoreOnBottomReached = useCallback(
		(containerRefElement?: HTMLDivElement | null) => {
			if (containerRefElement && fetchNextPage && data.length > 0) {
				const { scrollHeight, scrollTop, clientHeight } = containerRefElement
				//once the user has scrolled within 50px of the bottom of the table, fetch more data if we can
				if (scrollHeight - scrollTop - clientHeight < 50) {
					fetchNextPage()
				}
			}
		},
		[fetchNextPage, data.length]
	)

	useEffect(() => {
		if (useVirtual) {
			fetchMoreOnBottomReached(tableContainerRef.current)
		}
	}, [fetchMoreOnBottomReached, useVirtual])

	const headerContent = useMemo(() => {
		return headerGroups.map((headerGroup) => (
			<FlexDiv key={headerGroup.id} className="table-row">
				{headerGroup.headers.map((header) => {
					if (!header.column.getIsVisible()) return null
					return (
						<TableCellHead
							key={header.id}
							hideHeaders={!!hideHeaders}
							style={{ width: header.getSize(), flex: header.getSize() }}
							onClick={header.column.getToggleSortingHandler()}
							$canSort={header.column.getCanSort()}
						>
							{flexRender(header.column.columnDef.header, header.getContext())}
							{header.column.getCanSort() && (
								<SortIconContainer>
									{header.column.getIsSorted() ? (
										header.column.getIsSorted() === 'desc' ? (
											<StyledSortDownIcon />
										) : (
											<StyledSortUpIcon />
										)
									) : (
										<>
											<StyledSortUpIcon />
											<StyledSortDownIcon />
										</>
									)}
								</SortIconContainer>
							)}
						</TableCellHead>
					)
				})}
			</FlexDiv>
		))
	}, [hideHeaders, headerGroups])

	const renderTableBody = useCallback(() => {
		if (isLoading) {
			return (
				<LoadingContainer>
					<Loader />
				</LoadingContainer>
			)
		}

		if (!!noResultsMessage && data.length === 0) {
			return <NoResultsContainer>{noResultsMessage}</NoResultsContainer>
		}

		return (
			<div
				style={
					useVirtual
						? {
								height: `${rowVirtualizer.getTotalSize()}px`,
								position: 'relative',
							}
						: {
								overflowY: 'auto',
								overflowX: 'visible',
								maxHeight: `calc(100% - ${HEADER_HEIGHT})`,
							}
				}
				className="table-body"
			>
				{(useVirtual ? rowVirtualizer.getVirtualItems() : table.getRowModel().rows).map((item) => {
					const row = (useVirtual ? rows[item.index] : item) as Row<T>
					return (
						<Fragment key={row.id}>
							<TableBodyRow
								key={`${row.id}row`}
								highlightRowsOnHover={highlightRowsOnHover}
								row={row}
								onClick={onTableRowClick ? handleRowClick(row) : undefined}
								gridLayout={gridLayout}
								noBorder={noRowBorder}
								virtualPosition={useVirtual && 'start' in item ? item.start : undefined}
							/>
							{row.getIsExpanded() && (
								<div key={`${row.id}expanded`}>
									{SubRowComponent && <SubRowComponent row={row} />}
								</div>
							)}
						</Fragment>
					)
				})}
			</div>
		)
	}, [
		isLoading,
		noResultsMessage,
		data.length,
		useVirtual,
		rowVirtualizer,
		rows,
		table,
		highlightRowsOnHover,
		onTableRowClick,
		handleRowClick,
		gridLayout,
		noRowBorder,
		SubRowComponent,
	])

	const renderPagination = useCallback(() => {
		if (!shouldShowPagination && !paginationExtra && !CustomPagination) {
			return null
		}
		const paginationProps = {
			size: paginationSize,
			pageIndex: table.getState().pagination.pageIndex,
			pageCount: table.getPageCount(),
			canNextPage: table.getCanNextPage(),
			canPreviousPage: table.getCanPreviousPage(),
			setPage: table.setPageIndex,
			previousPage: table.previousPage,
			nextPage: table.nextPage,
			extra: paginationExtra,
		}

		if (CustomPagination) {
			return <CustomPagination {...paginationProps} />
		}

		return (
			<Pagination
				{...paginationProps}
				pageCount={paginationProps.pageCount === 0 ? 1 : paginationProps.pageCount}
			/>
		)
	}, [shouldShowPagination, paginationExtra, paginationSize, table, CustomPagination])

	return (
		<>
			<TableContainer
				ref={tableContainerRef}
				onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
				$height={containerHeight}
				$useVirtual={useVirtual}
			>
				<ReactTable
					$rounded={rounded}
					$noBottom={noBottom}
					className={className}
					$useVirtual={useVirtual}
				>
					{useVirtual ? (
						<StickyHeader className={DRAG_HANDLE_CLASS_NAME}>{headerContent}</StickyHeader>
					) : (
						headerContent
					)}
					{renderTableBody()}
					{!CustomPagination && renderPagination()}
				</ReactTable>
			</TableContainer>
			{CustomPagination && renderPagination()}
		</>
	)
}

const TableContainer = styled.div<{ $height?: number; $useVirtual?: boolean }>`
	overflow: auto;
	${(props) => (props.$height ? `height: ${props.$height}px;` : 'height: 100%;')}
	position: relative;
	${(props) =>
		props.$useVirtual
			? 'position: relative;'
			: `
		display: flex;
		flex: 1;
		overflow-x: auto;
`}
`

export const TableCellHead = styled(TableCell)<{ hideHeaders: boolean; $canSort: boolean }>`
	user-select: none;
	padding-left: 8px;
	&:first-child {
		padding-left: 18px;
	}
	&:last-child {
		padding-right: 18px;
	}
	${(props) => (props.hideHeaders ? 'display: none' : '')}
	${(props) =>
		props.$canSort &&
		css`
			cursor: pointer;
		`}

	${media.lessThan('lg')`
		padding-left: 0px;
	`}
`

const StickyHeader = styled.div`
	position: sticky;
	top: 0;
	z-index: 1;
	background-color: ${(props) => props.theme.colors.selectedTheme.darkBackground};
`

const NoResultsContainer = styled.div`
	display: flex;
	min-height: 100px;
	flex: 1;
	justify-content: center;
	border: none;
`

const LoadingContainer = styled.div`
	padding: 100px 0;
`

export const TableNoResults = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.text.label};
	font-size: 12px;
	padding: 30px 0;
	font-weight: 400;
`

const SortIconContainer = styled.span`
	display: flex;
	margin-left: 5px;
	flex-direction: column;
`

const ReactTable = styled.div<{ $rounded?: boolean; $noBottom?: boolean; $useVirtual?: boolean }>`
	border: ${(props) => props.theme.colors.selectedTheme.border};
	${(props) =>
		props.$noBottom &&
		css`
			border-bottom-width: 0;
		`};

	${(props) =>
		props.$rounded
			? css`
					border-radius: 8px;
			  `
			: css`
					border-left: none;
					border-right: none;
			  `};

	${(props) =>
		props.$useVirtual
			? css`
					display: grid;
			  `
			: css`
					display: flex;
					flex-direction: column;
					width: 100%;
					overflow: auto;
					position: relative;
			  `};

	${TableCell} {
		color: ${(props) => props.theme.colors.selectedTheme.text.value};
		font-size: 12px;
		height: ${HEADER_HEIGHT};
		font-weight: 400;
		font-feature-settings: 'tnum';
	}

	${TableCellHead} {
		color: ${(props) => props.theme.colors.selectedTheme.text.label};
		font-weight: 400;
		border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	}
`

const StyledSortDownIcon = styled(SortDownIcon)`
	width: 5px;
	height: 5px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
`

const StyledSortUpIcon = styled(SortUpIcon)`
	width: 5px;
	height: 5px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
`

export const TableHeader = styled(Body)<{ $small?: boolean }>`
	text-transform: capitalize;
	color: ${(props) => props.theme.colors.selectedTheme.text.label};

	${(props) =>
		props.$small &&
		css`
			font-size: 10px;
		`}
`

export const EmptyTableContainer = styled.div`
	display: flex;
	flex: 1;
	height: 100%;
	min-height: 100px;
	width: 100%;
	justify-content: center;
`

export default genericMemo(Table)
